import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EastIcon from '@mui/icons-material/East';
import EditIcon from '@mui/icons-material/Edit';
import LoopIcon from '@mui/icons-material/Loop';
import { Box, CardActionArea, CircularProgress, Collapse, Grid, IconButton, Paper, TableCell, TableRow, Tooltip, Typography, useTheme } from '@mui/material';
import moment from 'moment/moment';
import { darken } from 'polished';
import { Fragment, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'underscore';
import CalendarIcon from '../../icons/CalendarIcon';
import CalendarNoEndIcon from '../../icons/CalendarNoEndIcon';
import DateTimeIcon from '../../icons/DateTimeIcon';
import PlayIcon from '../../icons/PlayIcon';
import TimeIcon from '../../icons/TimeIcon';
import { MessageContext } from '../../utils/MessageContext';
import { UserContext } from '../../utils/UserContext';
import { useAxios } from '../../utils/useAxios';
import SoundDialog from '../SoundDialog';

export default function DashboardListItem({ item, index, drawer, paperWidth, desktop = false }) {
    const theme = useTheme();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState({
        "representation": false,
        "virtualRepresentation": false,
        "endDateTime": true,
        "endDate": false,
        "timeOnly": false
    });

    const { user, setUser } = useContext(UserContext);
    const { messages } = useContext(MessageContext);

    const soundRef = useRef();

    const axiosInstance = useAxios();

    const { t } = useTranslation();

    const isActive = (start, end) => {
        if (moment(start).isBefore(moment()) && !moment(end).isBefore(moment())) {
            return true;
        }
        return false;
    };

    const isAboveOneDay = (start, end) => {
        const diff = moment.duration(moment(end).diff(moment(start)));
        //handle no enddate
        if (!end) {
            return true;
        }
        if (diff.asHours() > 23) {
            return true;
        } else {
            return false;
        }
    }

    const getMessage = (companyMessageUid) => {
        const msg = messages?.filter(m => m?.companyMessageUid === companyMessageUid)[0];
        const newType = {
            representation: msg?.proxyMandatory,
            virtualRepresentation: msg?.virtualRepresentation,
            endDateTime: msg?.endDateTimeMandatory,
            endDate: msg?.endDateMandatory,
            timeOnly: msg?.timeOnlyMandatory
        };
        if (!_.isEqual(type, newType)) {
            setType({
                representation: msg?.proxyMandatory,
                virtualRepresentation: msg?.virtualRepresentation,
                endDateTime: msg?.endDateTimeMandatory,
                endDate: msg?.endDateMandatory,
                timeOnly: msg?.timeOnlyMandatory
            });
        };
        return msg;
    };

    const getTitle = (companyMessageUid) => {
        const message = getMessage(companyMessageUid);

        if (message?.timeOnlyMandatory) {
            return t("time-announcement");
        }
        if (message?.endDateTimeMandatory) {
            return t("datetime-announcement");
        }
        if (message?.endDateMandatory) {
            return t("date-announcement")
        }
        if (message?.timeOnlyMandatory === false && message?.endDateTimeMandatory === false && message?.endDateMandatory === false) {
            return t("without-enddate")
        }
        return null;
    };

    const getIcon = (companyMessageUid, item, color) => {
        const message = getMessage(companyMessageUid);

        if (message && !message.endDateMandatory && !message.endDateTimeMandatory && !message.timeOnlyMandatory) {
            return <CalendarNoEndIcon color={color} />
        }
        if (message && message?.endDateTimeMandatory) {
            return <DateTimeIcon color={color} bgColor={isActive(item?.startDate, item?.endDate) ? theme.palette.mode === "dark" ? theme.palette.success.dark : theme.palette.success.light : theme.palette.background.paper} />
        }
        if ((item && isAboveOneDay(item?.startDate, item?.endDate)) || (message && message?.endDateMandatory)) {
            return <CalendarIcon color={color} />
        }
        return <TimeIcon color={color} />
    };

    const getUntilOrSinceDate = (start, end) => {
        const roundToNearestWholeDay = Math.ceil(moment(start).diff(moment(), "days", true));

        if (moment(start).isSame(moment(), "day")) {
            return t("today");
        }
        if (isActive(start, end)) {
            if (roundToNearestWholeDay === 0) {
                return t("since-today");
            }
            if (roundToNearestWholeDay === -1) {
                return t("since-yesterday");
            }
            return t("since-x-days", { x: Math.abs(roundToNearestWholeDay) });
        }
        if (roundToNearestWholeDay === 1) {
            return t("tomorrow");
        }
        return t("in-x-days", { x: roundToNearestWholeDay });
    };

    const handleDelete = async (i) => {
        const newEntries = [...user.calendarEntries];
        const deleted = newEntries.splice(i, 1);

        await axiosInstance.current({
            url: "/entry",
            method: "DELETE",
            data: deleted
        })
        setUser({ ...user, calendarEntries: newEntries })
    }

    const getSound = async () => {
        setLoading(true);

        let entry = {
            companyMessageUid: item.companyMessageUid,
            startDate: item.startDate,
            endDate: item.endDate
        };

        if (item.representativeEmail !== null) {
            entry = { ...entry, representativeEmail: item.representativeEmail }
        }

        await axiosInstance.current({
            url: "sound-preview",
            method: "POST",
            data: entry
        }).then(res => {
            soundRef.current.open(res.data);
            setLoading(false);
        }).catch(err => {
            console.error(err.response);
            setLoading(false);
        })
    }

    const getDifference = (startDate, endDate, companyMessageUid) => {
        const message = getMessage(companyMessageUid);

        const differenceAsDays = moment(endDate).diff(moment(startDate), "days");
        const differenceAsHours = moment(endDate).diff(moment(startDate), "hours");
        const differenceAsMinutes = moment(endDate).diff(moment(startDate), "minutes");

        if (message?.endDateMandatory === false && message?.endDateTimeMandatory === false && message?.timeOnlyMandatory === false) {
            return t("indefinite")
        }
        if (differenceAsHours > 23 || message?.endDateMandatory || message?.endDateTimeMandatory) {
            const hours = Math.trunc(differenceAsHours - (24 * differenceAsDays));
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (Math.trunc(differenceAsDays) !== 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (Math.trunc(differenceAsDays) === 0 && hours !== 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            if (hours !== 0 && minutes === 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            if (hours === 0 && minutes !== 0 && message?.endDateMandatory === false) {
                return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")} & ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
            }
            return `${Math.trunc(differenceAsDays)} ${Math.trunc(differenceAsDays) === 1 ? t("day") : t("days")}`;
        } else if (differenceAsMinutes > 120) {
            const hours = Math.trunc(differenceAsHours);
            const minutes = Math.trunc(differenceAsMinutes - (60 * differenceAsHours));
            if (minutes === 0) {
                return `${hours} ${hours === 1 ? t("hour") : t("hours")}`;
            }
            return `${hours} ${hours === 1 ? t("hour") : t("hours")} ${t("and")} ${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        } else {
            const minutes = Math.trunc(differenceAsMinutes);
            return `${minutes} ${minutes === 1 ? t("minute") : t("minutes")}`;
        }
    }

    const formatTimeInformation = (start, end, companyMessageUid) => {
        let formattedStart;
        let formattedEnd;
        let duration;
        if (moment(start).isSame(moment(), "days")) {
            formattedStart = moment(start).format("HH:mm");
        } else {
            formattedStart = moment(start).format("DD.MM.YYYY HH:mm");
        }
        if (!end) {
            return `${formattedStart} - ${t("no-end")}`;
        }
        if (moment(end).isSame(moment(), "days")) {
            formattedEnd = moment(end).format("HH:mm");
        } else {
            formattedEnd = moment(end).format("DD.MM.YYYY HH:mm");
        }
        duration = getDifference(start, end, companyMessageUid);
        return `${formattedStart} - ${formattedEnd} ${duration ? `(${duration})` : ""}`;
    };

    const renderDateTime = (date) => (
        <Fragment>
            <Typography noWrap sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 15, lineHeight: 1.25 }}>
                {moment(date).format("DD.MM.YYYY")}
            </Typography>
            <Typography noWrap sx={{ color: isActive(item?.startDate, item?.endDate) ? "#fff" : "text.primary", fontSize: 15, lineHeight: 1.25 }}>
                {moment(date).format("HH:mm")}
            </Typography>
        </Fragment>
    );

    const getRepeatLabel = (repeatType, compact = false) => {
        switch (Number(repeatType)) {
            case 1:
                return compact ? t("repeat-weekdays") : t("daily-without-weekend");
            case 2:
                return compact ? t("repeat-daily") : t("daily-with-weekend");
            case 3:
                return t("weekly");
            case 6:
                return t("annually");
            default:
                return "";
        }
    };

    const active = isActive(item?.startDate, item?.endDate);
    const hasRepeat = Number(item?.repeatType) > 0;
    const repeatLabel = getRepeatLabel(item?.repeatType);
    const compactRepeatLabel = getRepeatLabel(item?.repeatType, true);
    const secondaryColor = active ? darken(0.12, "#fff") : theme.palette.mode === "light" && theme.palette.primary.main === "#0075FF" ? "#4F4F4F" : "text.secondary";

    const fontSize = desktop ? "16px" : "14px";
    const iconFontSize = desktop ? 16 : 15;
    const expandedHeight = desktop ? hasRepeat ? 304 : 280 : hasRepeat ? 284 : 260;
    const actionsHeight = desktop ? hasRepeat ? 204 : 180 : hasRepeat ? 190 : 165;

    const actionButtons = desktop ? (
        <Grid container direction="column" alignContent="center">
            <Grid item>
                <Tooltip title={t("edit")} placement="right">
                    <IconButton onClick={() => { drawer(item) }} sx={{ mt: .5 }}>
                        <EditIcon sx={{ color: active ? "#fff" : "text.primary", fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid item>
                {!loading ?
                    <Tooltip title={t("test-listen")} placement="right">
                        <Box
                            sx={{
                                my: 2.25,
                                display: "flex",
                                justifyContent: "center"
                            }}
                        >
                            <IconButton onClick={() => { getSound(); }} sx={{ p: .75, border: 1.5, borderColor: active ? "#fff" : "text.primary" }}>
                                <PlayIcon color={active ? "#fff" : theme.palette.text.primary} fontSize={16} />
                            </IconButton>
                        </Box>
                    </Tooltip> :
                    <CircularProgress color="inherit" size={24} sx={{ my: 2.3, ml: .5 }} />
                }
            </Grid>
            <Grid item>
                <Tooltip title={t("delete")} placement="right">
                    <IconButton onClick={() => { handleDelete(index) }} sx={{ color: active ? "#fff" : "text.primary", "&:hover": { color: "error.main" } }}>
                        <DeleteIcon sx={{ color: "inherit", fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Grid>
        </Grid>
    ) : (
        <Grid container direction="column">
            <Tooltip title={t("edit")} placement="right">
                <IconButton onClick={() => { drawer(item) }} sx={{ mx: 2 }}>
                    <EditIcon sx={{ color: active ? "#fff" : "text.primary", fontSize: 18 }} />
                </IconButton>
            </Tooltip>
            {!loading ?
                <Tooltip title={t("test-listen")} placement="right">
                    <Box
                        sx={{
                            height: 32,
                            my: 3,
                            display: "flex",
                            justifyContent: "center"
                        }}
                    >
                        <IconButton onClick={() => { getSound(); }} sx={{ p: 1, border: 1.5, borderColor: active ? "#fff" : theme.palette.text.primary }}>
                            <PlayIcon color={active ? "#fff" : theme.palette.text.primary} fontSize={14} />
                        </IconButton>
                    </Box>
                </Tooltip> :
                <CircularProgress color="inherit" size={26} sx={{ my: 3.33, ml: 3 }} />
            }
            <Tooltip title={t("delete")} placement="right">
                <IconButton onClick={() => { handleDelete(index) }} sx={{ color: active ? "#fff" : "text.primary", "&:hover": { color: "error.main" }, mx: 2 }}>
                    <DeleteIcon sx={{ color: "inherit", fontSize: 18 }} />
                </IconButton>
            </Tooltip>
        </Grid>
    );

    return (
        <TableRow key={index} sx={desktop ? { "&:first-of-type": { pt: 1 }, "&:last-child td, &:last-child th": { border: 0 } } : { "&:last-child td, &:last-child th": { border: 0 } }}>
            <TableCell sx={{ p: 0, border: 0 }}>
                <Paper
                    elevation={desktop ? 1 : theme.palette.mode === "dark" ? 1 : 0}
                    sx={{
                        borderRadius: 3,
                        mb: 1,
                        bgcolor: active ? theme.palette.mode === "dark" ? "success.dark" : "success.light" : "background.paper",
                        ...(desktop ? {
                            width: paperWidth * .5 - 20
                        } : {
                            border: theme.palette.mode === "dark" ? 0 : 1.5,
                            borderColor: "divider"
                        })
                    }}
                >
                    <Collapse in={open} collapsedSize={70}>
                        <Box height={expandedHeight}>
                            <CardActionArea sx={{ borderRadius: 3 }}>
                                <Box
                                    onClick={() => { setOpen(!open) }}
                                    sx={{
                                        height: 70,
                                        width: "100%",
                                        boxSizing: "border-box",
                                        color: active ? "#fff" : "text.primary",
                                        px: 2,
                                        display: "grid",
                                        gridTemplateColumns: "48px minmax(0, 1fr) 32px",
                                        alignItems: "center",
                                        columnGap: 1.5,
                                        borderBottom: open ? 1 : 0,
                                        borderBottomColor: "divider",
                                        cursor: "pointer"
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            "& .MuiSvgIcon-root": {
                                                m: 0,
                                                mt: 0,
                                                display: "block"
                                            }
                                        }}
                                    >
                                        {getIcon(item.companyMessageUid, item, active ? "#fff" : theme.palette.text.primary)}
                                    </Box>
                                    <Box
                                        sx={{
                                            minWidth: 0,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "center",
                                            gap: open && item.optionalDisplayName === undefined ? 0 : 0.25
                                        }}
                                    >
                                        <Typography noWrap sx={{ width: "100%", fontWeight: open ? 700 : 400, fontSize: open && item.optionalDisplayName === undefined ? "22px" : fontSize, overflow: "hidden", textOverflow: "ellipsis", fontStyle: "normal", lineHeight: 1.2 }}>{item.optionalDisplayName ? open ? item.optionalDisplayName : `${item.optionalDisplayName} ${getUntilOrSinceDate(item.startDate, item.endDate)}` : getUntilOrSinceDate(item.startDate, item.endDate)}</Typography>
                                        <Box
                                            sx={{
                                                width: "100%",
                                                display: open && item.optionalDisplayName === undefined ? "none" : "flex",
                                                alignItems: "center",
                                                gap: 0.75,
                                                minWidth: 0,
                                                color: secondaryColor,
                                                overflow: "hidden"
                                            }}
                                        >
                                            <Typography noWrap sx={{ flex: "1 1 auto", minWidth: 0, fontSize, overflow: "hidden", textOverflow: "ellipsis", fontStyle: "normal", lineHeight: 1.2 }}>
                                                {open && item.optionalDisplayName ? getUntilOrSinceDate(item.startDate, item.endDate) : formatTimeInformation(item.startDate, item.endDate, item.companyMessageUid)}
                                            </Typography>
                                            {hasRepeat && !open &&
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        flex: "0 0 auto",
                                                        maxWidth: "42%",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 0.35,
                                                        color: "inherit",
                                                        overflow: "hidden"
                                                    }}
                                                >
                                                    <LoopIcon sx={{ color: "inherit", fontSize: iconFontSize }} />
                                                    <Typography noWrap component="span" sx={{ color: "inherit", fontSize, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {compactRepeatLabel}
                                                    </Typography>
                                                </Box>
                                            }
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}
                                    >
                                        <ArrowBackIosNewIcon sx={{ color: active ? "#fff" : "primary.main", fontSize: 19, transform: open ? "rotate(90deg)" : "rotate(180deg)", transition: "transform 200ms ease" }} />
                                    </Box>
                                </Box>
                            </CardActionArea>
                            <Box sx={{ display: "flex", width: "100%" }}>
                                <Box sx={{ width: "80%", minWidth: 0, boxSizing: "border-box", pl: 2 }}>
                                    <Box sx={{ height: desktop ? 52 : 46, borderBottom: desktop ? 0 : 1, borderColor: "divider", display: "flex", alignItems: "center" }}>
                                        <Typography noWrap sx={{ width: "95%", color: active ? "#fff" : "text.primary", lineHeight: 1.25 }}>{getTitle(item?.companyMessageUid)}</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            minHeight: desktop ? 88 : 72,
                                            py: desktop ? 2 : 1.5,
                                            pr: 1,
                                            boxSizing: "border-box",
                                            borderBottom: 1,
                                            borderTop: desktop ? 1 : 0,
                                            borderColor: "divider",
                                            display: "flex",
                                            alignItems: "center"
                                        }}
                                    >
                                        <Box sx={{ flex: "1 1 0", minWidth: 0, textAlign: "center" }}>
                                            {renderDateTime(item.startDate)}
                                        </Box>
                                        <Box sx={{ flex: desktop ? "0 0 40px" : "0 0 36px", color: active ? "#fff" : "text.primary", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <EastIcon />
                                        </Box>
                                        <Box sx={{ flex: "1 1 0", minWidth: 0, textAlign: "center" }}>
                                            {item.endDate ?
                                                renderDateTime(item.endDate)
                                                :
                                                <Typography sx={{ color: active ? "#fff" : "text.primary", fontSize: 14, lineHeight: 1.25, overflowWrap: "anywhere" }}>
                                                    {t("no-enddate")}
                                                </Typography>
                                            }
                                        </Box>
                                    </Box>
                                    {hasRepeat &&
                                        <Box
                                            sx={{
                                                minHeight: 34,
                                                py: 0.75,
                                                pr: 1,
                                                boxSizing: "border-box",
                                                borderBottom: 1,
                                                borderColor: "divider",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                color: active ? "#fff" : "text.primary"
                                            }}
                                        >
                                            <LoopIcon sx={{ flex: "0 0 auto", color: "inherit", fontSize: 16 }} />
                                            <Typography noWrap sx={{ flex: "1 1 auto", minWidth: 0, color: "inherit", fontSize: 14, fontWeight: 500 }}>
                                                {repeatLabel}
                                            </Typography>
                                        </Box>
                                    }
                                    {item.representation ?
                                        <Fragment>
                                            <Typography noWrap={desktop} sx={{ color: active ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>{t("representation")}</Typography>
                                            <Typography noWrap={desktop} sx={{ color: active ? "#fff" : "text.primary", fontSize: 14 }}>{item.representation.length > 28 ? `${item.representation.substring(0, 28)} ...` : item.representation}</Typography>
                                        </Fragment>
                                        :
                                        <Typography noWrap={desktop} sx={{ color: active ? "#fff" : "text.secondary", fontSize: 12, mt: 1 }}>{t("no-representation")}</Typography>
                                    }
                                </Box>
                                <Box
                                    height={actionsHeight}
                                    visibility={open ? "visible" : "hidden"}
                                    sx={{
                                        width: "20%",
                                        flex: "0 0 20%",
                                        boxSizing: "border-box",
                                        color: active ? "#fff" : "text.primary",
                                        borderLeft: 1,
                                        borderColor: "divider",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        ...(desktop ? { pr: 2 } : {})
                                    }}
                                >
                                    {actionButtons}
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </Paper>
            </TableCell>
            <SoundDialog message={getTitle(item.companyMessageUid)} startDate={item.startDate} endDate={item.endDate} noEnd={!type.endDate && !type.endDateTime && !type.timeOnly} ref={soundRef} />
        </TableRow>
    )
}
